import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { MarketplaceArche } from '@/lib/api';

interface SearchResultsContextValue {
  searchResults: MarketplaceArche[];
  searchQuery: string | null;
  isSearching: boolean;
  setSearchResults: (results: MarketplaceArche[], query: string) => void;
  clearSearchResults: () => void;
  setIsSearching: (searching: boolean) => void;
}

const SearchResultsContext = createContext<SearchResultsContextValue | null>(null);

export function SearchResultsProvider({ children }: { children: ReactNode }) {
  const [searchResults, setSearchResultsState] = useState<MarketplaceArche[]>([]);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const setSearchResults = useCallback((results: MarketplaceArche[], query: string) => {
    setSearchResultsState(results);
    setSearchQuery(query);
  }, []);

  const clearSearchResults = useCallback(() => {
    setSearchResultsState([]);
    setSearchQuery(null);
  }, []);

  return (
    <SearchResultsContext.Provider
      value={{
        searchResults,
        searchQuery,
        isSearching,
        setSearchResults,
        clearSearchResults,
        setIsSearching,
      }}
    >
      {children}
    </SearchResultsContext.Provider>
  );
}

export function useSearchResults() {
  const context = useContext(SearchResultsContext);
  if (!context) {
    throw new Error('useSearchResults must be used within a SearchResultsProvider');
  }
  return context;
}
